/**
 * FileAttachmentZone — drop zone + list + draft state machine.
 *
 * Wraps `FileDropZone` and `FileAttachmentRow` to provide the canonical
 * "stage locally, commit on parent save" UX:
 *
 * 1. User drops a file → it shows at once as a row with a progress ring
 *    (`useUploads`) and uploads to ``/api/v1/upload`` (the blob lands under
 *    ``tenants/{slug}/temp/`` thanks to the backend forcing
 *    ``category=temp``), retried on a network drop. Once it lands, the
 *    returned URL is held in ``pendingAdds`` until the parent commits via
 *    its own save. A failed one stays as a row with «Reintentar».
 * 2. User clicks delete on a *pending* file → ``DELETE /api/v1/upload?url=…``
 *    immediately + remove from ``pendingAdds``. No state stays inconsistent.
 * 3. User clicks delete on an *existing* file → push id to ``pendingRemovals``
 *    (visual: red border + undo). Nothing happens server-side until the
 *    parent commits, which is the moment the parent's PUT detects the
 *    missing file in its payload and tears down the row + blob.
 * 4. User clicks undo → remove id from ``pendingRemovals``.
 *
 * Lifecycle hooks via `useImperativeHandle`:
 *
 * - ``discardPending()`` — DELETE all pending temp blobs, clear state.
 *   Call from the parent's cancel/close handler.
 * - ``markCommitted()`` — clear state WITHOUT DELETEs. Call after a
 *   successful parent save (the temps were just promoted to permanent).
 * - ``hasPendingChanges()`` — for combining with `useDirtyTracker`.
 *
 * Orphan safety: the backend runs a daily sweep that purges anything
 * under any tenant's ``temp/`` folder older than 24 h, so a tab close
 * without explicit discard is always cleaned up eventually.
 */
import React, { useCallback, useImperativeHandle, useRef, useState } from 'react';
import FileAttachmentRow from './FileAttachmentRow';
import FileDropZone from './DropZonePanel';
import {
    defaultMaxSize,
    inputAcceptFor,
    summarizeRejections,
    validateFiles,
    type AcceptMode,
} from '../lib/fileValidation';
import type { IconName } from './Icon';
import { ensureFileReadable } from '../lib/fileUpload';
import { browserRetryEnv, uploadMultipartWithRetry } from '../lib/upload/retry';
import { defaultUploadErrorText, type UploadErrorTextFn } from '../lib/upload/errorText';
import { browserPostForm } from '../lib/upload/xhr';
import { useUploads, type UploadRunner } from '../hooks/useUploads';
import { useToast } from '../contexts/ToastContext';

/** Caller-supplied entry for an already-saved attached file. Parents map
 *  their own field names (`file_name`/`name`, `file_url`/`url`, …) into
 *  this normalised shape — keeps the component free of per-domain
 *  coupling. */
export interface AttachedFileEntry {
    id: string | number;
    name: string;
    url: string;
    size?: string;
    type?: 'image' | 'pdf' | 'stl' | 'other' | string;
}

/** A file uploaded to the temp folder, awaiting commit by the parent. */
export interface PendingFile {
    /** Stable client-side identifier for the row (not persisted). */
    tempId: string;
    file_url: string;
    file_name: string;
    file_size: string;
    file_type: string;
}

export interface FileAttachmentZoneState {
    pendingAdds: PendingFile[];
    pendingRemovals: (string | number)[];
    isDirty: boolean;
}

export interface FileAttachmentZoneHandle {
    /** DELETE all pending temp uploads + clear state. Call on cancel. */
    discardPending: () => Promise<void>;
    /** Clear state without server-side cleanup. Call after a successful
     *  parent save (temps were just moved to permanent storage). */
    markCommitted: () => void;
    hasPendingChanges: () => boolean;
    /** Inject a pending file from outside the dropzone. Used by callers
     *  that upload via a custom flow (e.g. Budget PDF generation in
     *  orders) and need the result to live in the zone's pending state
     *  so it gets persisted on the next save and cleaned up on cancel. */
    addPending: (entry: PendingFile) => void;
    /** Mark an existing file (one already in `existingFiles`) for
     *  removal at next save, just as if the user had clicked the
     *  in-row delete button. Used by callers that want to atomically
     *  swap an existing entry (mark old as removed + addPending new). */
    markRemoval: (id: string | number) => void;
    /** Snapshot of the current pending state. Read-only — use this when
     *  composing the save payload from a parent that doesn't subscribe
     *  to ``onChange`` (e.g. an imperative ``handleSave`` flow). */
    getState: () => FileAttachmentZoneState;
    /** Upload a File to the temp store and stage it as a pending add.
     *  For callers that have a File in hand outside the drop flow (e.g. the
     *  "Subir con IA" button, which analyses then attaches the same file). */
    uploadAndAdd: (file: File) => Promise<void>;
}

export interface FileAttachmentZoneProps {
    /** Files already saved in the database. The parent owns this list. */
    existingFiles: AttachedFileEntry[];
    /** Fired on every state change. Parent typically: (a) recomputes the
     *  save payload by composing existing - pendingRemovals + pendingAdds,
     *  and (b) feeds `state.isDirty` into its `useDirtyTracker`. */
    onChange?: (state: FileAttachmentZoneState) => void;
    /** Validation mode — drives client-side filtering AND server-side
     *  validation (sent as ``?accept=``). Default ``'any'``. */
    accept?: AcceptMode;
    /** Max size per file in bytes. Defaults based on `accept`. */
    maxSize?: number;
    /** Max total files (existing + pending). */
    maxCount?: number;
    /** Floating label rendered above the dropzone (uses the same
     *  pattern as Input/Select). */
    label?: string;
    /** Empty-state text shown inside the dropzone when there are no
     *  files yet. Default "Subir archivos". */
    emptyLabel?: string;
    /** Compact density for the rows (smaller text, hides size). */
    compact?: boolean;
    /** Material icon for the empty state. Default 'cloud_upload'. */
    icon?: IconName;
    className?: string;
    /** Optional 3D viewer callback. When set, .stl/.3mf rows get a
     *  "view_in_ar" button before the download icon. The callback is
     *  responsible for rendering the viewer (modal, navigation, …). */
    onPreview3D?: (file: { url?: string; name: string }) => void;
    /** Optional tooltip translations — pass `t()` values. */
    preview3DAriaLabel?: string;
    downloadAriaLabel?: string;
    deleteAriaLabel?: string;
    undoAriaLabel?: string;
    /** Builds the toast message shown when an upload fails. Receives the
     *  file name. Default Spanish (`Error al subir {name}`). Only the last
     *  resort of the default `uploadErrorText`. */
    uploadErrorLabel?: (name: string) => string;
    /** Turns a failed upload into what the person reads — the toast (with its
     *  «Ver detalle técnico» fold) and the failed row's tooltip. Default
     *  (Admin, Spanish): the refusal's own `message`, a cut connection in plain
     *  words, else `uploadErrorLabel`. The tenant passes its translating
     *  `uploadErrorText(t)` (`services/uploadErrors.ts`). */
    uploadErrorText?: UploadErrorTextFn;
    /** Base URL for the temp upload endpoint. Default ``/api/v1`` (tenant).
     *  Admin passes ``/api`` (its upload routes live under ``/api/...``). */
    uploadBaseUrl?: string;
    /** Auth model for the upload/delete calls. ``'bearer'`` (default) reads
     *  the JWT from localStorage (tenant). ``'cookie'`` sends credentials
     *  with no Authorization header (admin's ``bp_platform`` cookie). */
    uploadAuthMode?: 'bearer' | 'cookie';
    /** Imperative handle for discard/markCommitted (see interface above). */
    ref?: React.Ref<FileAttachmentZoneHandle>;
}

const formatBytes = (bytes: number): string => {
    if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / 1024).toFixed(1) + ' KB';
};

const inferType = (filename: string, mime: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase() ?? '';
    if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
    if (ext === 'pdf' || mime === 'application/pdf') return 'pdf';
    if (['stl', 'obj', '3mf', 'step', 'stp', 'gcode', 'bgcode'].includes(ext)) return 'stl';
    return 'other';
};

const FileAttachmentZone = React.forwardRef<FileAttachmentZoneHandle, Omit<FileAttachmentZoneProps, 'ref'>>(
    function FileAttachmentZone(
        {
            existingFiles,
            onChange,
            accept = 'any',
            maxSize,
            maxCount,
            label,
            emptyLabel,
            compact = false,
            icon,
            className = '',
            onPreview3D,
            preview3DAriaLabel,
            downloadAriaLabel,
            deleteAriaLabel,
            undoAriaLabel,
            uploadErrorLabel = (name: string) => `Error al subir ${name}`,
            uploadErrorText,
            uploadBaseUrl = '/api/v1',
            uploadAuthMode = 'bearer',
        },
        ref,
    ) {
        const { toast } = useToast();
        const [pendingAdds, setPendingAdds] = useState<PendingFile[]>([]);
        const [pendingRemovals, setPendingRemovals] = useState<(string | number)[]>([]);
        const inputRef = useRef<HTMLInputElement>(null);
        // Stable refs for the imperative handle so callers don't need
        // to re-bind across renders.
        const pendingAddsRef = useRef<PendingFile[]>([]);
        pendingAddsRef.current = pendingAdds;
        const pendingRemovalsRef = useRef<(string | number)[]>([]);
        pendingRemovalsRef.current = pendingRemovals;

        const emit = useCallback(
            (adds: PendingFile[], removals: (string | number)[]) => {
                onChange?.({
                    pendingAdds: adds,
                    pendingRemovals: removals,
                    isDirty: adds.length > 0 || removals.length > 0,
                });
            },
            [onChange],
        );

        // Auth + base-url shaping for the upload/delete calls so the same
        // component works in the tenant (Bearer, /api/v1) and admin (cookie,
        // /api). Spread the return into the fetch init.
        const buildReq = (): { headers: HeadersInit; credentials?: RequestCredentials } => {
            if (uploadAuthMode === 'cookie') return { headers: {}, credentials: 'include' };
            const token = localStorage.getItem('token');
            const headers: HeadersInit = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;
            return { headers };
        };

        const runUpload: UploadRunner<{ url: string }> = (file, { onState, signal }) =>
            uploadMultipartWithRetry(
                file,
                {
                    url: `${uploadBaseUrl}/upload?category=temp&accept=${encodeURIComponent(accept)}`,
                    ...(uploadAuthMode === 'cookie'
                        ? { withCredentials: true }
                        : { headers: buildReq().headers as Record<string, string> }),
                },
                { readable: ensureFileReadable, post: browserPostForm() },
                browserRetryEnv(),
                // Refused before sending, as the server's own `file_too_large_max`.
                { onState, signal, maxBytes: maxSize ?? defaultMaxSize(accept) },
            ) as Promise<{ url: string }>;

        const failureText = (err: unknown, fileName: string) =>
            uploadErrorText
                ? uploadErrorText(err, fileName)
                : defaultUploadErrorText(err, fileName, uploadErrorLabel);

        const uploads = useUploads(runUpload, {
            onDone: (item, json) => {
                const entry: PendingFile = {
                    tempId: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                    file_url: json.url,
                    file_name: item.file.name,
                    file_size: formatBytes(item.file.size),
                    file_type: inferType(item.file.name, item.file.type),
                };
                const next = [...pendingAddsRef.current, entry];
                pendingAddsRef.current = next;
                setPendingAdds(next);
                emit(next, pendingRemovalsRef.current);
            },
            onError: (item, err) => {
                const { text, detail } = failureText(err, item.file.name);
                toast.error(text, { detail: detail ?? null });
            },
            errorMessage: (err, item) => failureText(err, item.file.name).text,
        });
        // Read by `discardPending`, which must stop uploads still on their way.
        const uploadItemsRef = useRef(uploads.items);
        uploadItemsRef.current = uploads.items;

        const handleFiles = (files: File[]) => {
            if (files.length === 0) return;
            void uploads.start(files);
        };

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const list = e.target.files;
            if (!list || list.length === 0) return;
            const arr = Array.from(list);
            // Reset input so the same file can be re-selected later.
            if (e.target) e.target.value = '';
            // The same checks as a drop (FileDropZone): all-or-nothing.
            const result = validateFiles(arr, {
                accept,
                maxSize,
                maxCount,
                existingCount: existingFiles.length + pendingAdds.length + uploads.items.length,
            });
            if (!result.allValid) {
                toast.error(summarizeRejections(result.rejected));
                return;
            }
            handleFiles(result.accepted);
        };

        const removePending = async (tempId: string) => {
            const target = pendingAdds.find((p) => p.tempId === tempId);
            if (!target) return;
            const next = pendingAdds.filter((p) => p.tempId !== tempId);
            setPendingAdds(next);
            emit(next, pendingRemovals);
            // Best-effort backend cleanup of the temp blob.
            try {
                await fetch(
                    `${uploadBaseUrl}/upload?url=${encodeURIComponent(target.file_url)}`,
                    { method: 'DELETE', ...buildReq() },
                );
            } catch {
                /* the daily sweep is the safety net */
            }
        };

        const markRemoval = (id: string | number) => {
            if (pendingRemovals.includes(id)) return;
            const next = [...pendingRemovals, id];
            setPendingRemovals(next);
            emit(pendingAdds, next);
        };

        const undoRemoval = (id: string | number) => {
            const next = pendingRemovals.filter((x) => x !== id);
            setPendingRemovals(next);
            emit(pendingAdds, next);
        };

        useImperativeHandle(
            ref,
            () => ({
                discardPending: async () => {
                    // First stop what is still uploading: one that lands after
                    // this would enter the form the user just discarded.
                    for (const item of uploadItemsRef.current) uploads.cancel(item.id);
                    const toDelete = pendingAddsRef.current;
                    setPendingAdds([]);
                    setPendingRemovals([]);
                    emit([], []);
                    if (toDelete.length === 0) return;
                    await Promise.allSettled(
                        toDelete.map((p) =>
                            fetch(
                                `${uploadBaseUrl}/upload?url=${encodeURIComponent(p.file_url)}`,
                                { method: 'DELETE', ...buildReq() },
                            ),
                        ),
                    );
                },
                markCommitted: () => {
                    setPendingAdds([]);
                    setPendingRemovals([]);
                    emit([], []);
                },
                hasPendingChanges: () =>
                    pendingAddsRef.current.length > 0 || pendingRemovalsRef.current.length > 0,
                addPending: (entry: PendingFile) => {
                    const next = [...pendingAddsRef.current, entry];
                    setPendingAdds(next);
                    emit(next, pendingRemovalsRef.current);
                },
                markRemoval: (id: string | number) => {
                    if (pendingRemovalsRef.current.includes(id)) return;
                    const next = [...pendingRemovalsRef.current, id];
                    setPendingRemovals(next);
                    emit(pendingAddsRef.current, next);
                },
                getState: () => ({
                    pendingAdds: pendingAddsRef.current,
                    pendingRemovals: pendingRemovalsRef.current,
                    isDirty:
                        pendingAddsRef.current.length > 0 ||
                        pendingRemovalsRef.current.length > 0,
                }),
                uploadAndAdd: async (file: File) => {
                    await uploads.start([file]);
                },
            }),
            // All reads go through stable refs (pendingAddsRef /
            // pendingRemovalsRef) so the handle never goes stale even
            // when deps don't change. ``emit`` is the only closed-over
            // value that may shift (it shifts when `onChange` shifts,
            // which is the parent's responsibility to memoise).
            [emit, uploads.start, uploads.cancel],
        );

        const totalCount = existingFiles.length + pendingAdds.length + uploads.items.length;
        const hasFiles = totalCount > 0;

        const list = (
            <ul className="space-y-1">
                {existingFiles.map((f) => {
                    const isMarkedForRemoval = pendingRemovals.includes(f.id);
                    return (
                        <li key={`exist-${f.id}`}>
                            <FileAttachmentRow
                                name={f.name}
                                type={f.type}
                                url={f.url}
                                onPreview3D={onPreview3D ? () => onPreview3D({ url: f.url, name: f.name }) : undefined}
                                state={isMarkedForRemoval ? 'deleted' : undefined}
                                onDelete={isMarkedForRemoval ? undefined : () => markRemoval(f.id)}
                                onUndoDelete={isMarkedForRemoval ? () => undoRemoval(f.id) : undefined}
                                preview3DAriaLabel={preview3DAriaLabel}
                                downloadAriaLabel={downloadAriaLabel}
                                deleteAriaLabel={deleteAriaLabel}
                                undoAriaLabel={undoAriaLabel}
                            />
                        </li>
                    );
                })}
                {uploads.items.map((u) => (
                    <li key={u.id}>
                        <FileAttachmentRow
                            name={u.file.name}
                            type={inferType(u.file.name, u.file.type)}
                            upload={u.progress}
                            onRetryUpload={() => uploads.retry(u.id)}
                            onCancelUpload={() => uploads.cancel(u.id)}
                        />
                    </li>
                ))}
                {pendingAdds.map((p) => (
                    <li key={`pend-${p.tempId}`}>
                        <FileAttachmentRow
                            name={p.file_name}
                            type={p.file_type}
                            url={p.file_url}
                            onPreview3D={onPreview3D ? () => onPreview3D({ url: p.file_url, name: p.file_name }) : undefined}
                            state="pending"
                            onDelete={() => removePending(p.tempId)}
                            preview3DAriaLabel={preview3DAriaLabel}
                            downloadAriaLabel={downloadAriaLabel}
                            deleteAriaLabel={deleteAriaLabel}
                        />
                    </li>
                ))}
            </ul>
        );

        const wrapper = label ? `pt-2 relative ${className}` : className;

        return (
            <div className={wrapper}>
                {label && (
                    <span className="absolute top-0 left-2 px-1 bg-white dark:bg-surface-dark text-[10px] font-bold text-primary uppercase tracking-wider z-[1] pointer-events-none truncate max-w-[calc(100%-1rem)]">
                        {label}
                    </span>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept={inputAcceptFor(accept)}
                    className="hidden"
                    onChange={handleInputChange}
                />
                <FileDropZone
                    accept={accept}
                    maxSize={maxSize}
                    maxCount={maxCount}
                    existingCount={totalCount}
                    variant={compact ? 'compact' : 'full'}
                    icon={icon ?? 'cloud_upload'}
                    label={emptyLabel}
                    onClick={() => inputRef.current?.click()}
                    onFileDrop={(files) => handleFiles(files)}
                >
                    {hasFiles ? list : undefined}
                </FileDropZone>
            </div>
        );
    },
);

export default FileAttachmentZone;
